import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Pill,
  Syringe,
  Ruler,
  CalendarClock,
  Users,
  LogOut,
  ChevronRight,
  HeartPulse,
  ShieldAlert,
  ShieldCheck,
  PhoneCall,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/profiles", label: "Family", icon: Users, id: "nav-profiles" },
  { to: "/medications", label: "Medications", icon: Pill, id: "nav-medications" },
  { to: "/vaccinations", label: "Vaccinations", icon: Syringe, id: "nav-vaccinations" },
  { to: "/growth", label: "Growth", icon: Ruler, id: "nav-growth" },
  { to: "/appointments", label: "Appointments", icon: CalendarClock, id: "nav-appointments" },
  { to: "/sick-kid", label: "Sick Mode", icon: HeartPulse, id: "nav-sick" },
  { to: "/insurance", label: "Insurance", icon: ShieldCheck, id: "nav-insurance" },
  { to: "/contacts", label: "Contacts", icon: PhoneCall, id: "nav-contacts" },
  { to: "/emergency", label: "Emergency", icon: ShieldAlert, id: "nav-emergency" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);

  const loadProfiles = useCallback(async () => {
    const { data, error } = await supabase
      .from("family_profiles")
      .select("*")
      .order("created_at", { ascending: true });
    if (!error) setProfiles(data || []);
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card md:flex">
        <div className="border-b border-border px-6 py-5">
          <Link to="/dashboard" data-testid="sidebar-logo-link">
            <Logo size="md" />
          </Link>
        </div>
        <nav className="flex-1 space-y-1 p-3" data-testid="sidebar-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.id}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground shadow-soft"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              <span>{item.label}</span>
              <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-0 group-hover:opacity-60" />
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="mb-2 flex items-center gap-3 rounded-xl bg-secondary/60 p-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-foreground">{user?.name}</div>
              <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={async () => {
              navigate("/", { replace: true });
              await logout();
            }}
            data-testid="logout-btn"
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 md:hidden">
          <Logo size="sm" />
          <button
            onClick={async () => {
              navigate("/", { replace: true });
              await logout();
            }}
            data-testid="logout-btn-mobile"
            className="rounded-lg p-2 text-muted-foreground hover:bg-secondary"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </header>

        <main className="flex-1 p-4 md:p-8">
          <Outlet context={{ profiles, refreshProfiles: loadProfiles }} />
        </main>

        {/* Mobile bottom nav (top 5 most-used) */}
        <nav className="sticky bottom-0 z-30 grid grid-cols-5 border-t border-border bg-card md:hidden">
          {[NAV[0], NAV[1], NAV[6], NAV[9], NAV[5]].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`${item.id}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2 text-[10px] font-medium ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
